<script lang="ts">
  import { glanceRow } from '../forecast/glance'
  import { geoPermission } from '../geolocation'
  import { haptic, isNativeShell } from '../native'
  import { glanceFor, watchGlances, type GlanceEntry } from '../state/glance.svelte'
  import { here, locate, openPlace } from '../state/here.svelte'
  import { location } from '../state/location.svelte'
  import { prefs } from '../state/prefs.svelte'
  import { MAX_PLACES, places, type SavedPlace } from '../state/places.svelte'
  import { disableAlerts, enableAlerts, push, pushSupport } from '../state/push.svelte'
  import { shownPlace } from '../state/shown'
  import { whileVisible } from '../visibility'
  import Icon from './Icon.svelte'

  /**
   * Places with their weather (Phase 11 plan §6.3): My location, then every
   * saved place, each a tone block with the temperature, the name, the sky
   * or the leading alert, today's high and low, and the bell. One
   * `/api/glance` per row, asked while this is mounted and never otherwise.
   */

  interface Props {
    /** After a pick: the phone opens Forecast. */
    onPicked?: () => void
    /** Rename, reorder and share live in Settings. */
    onManage?: () => void
  }

  let { onPicked, onManage }: Props = $props()

  /**
   * Location can be had at all. The permission is asked again whenever the
   * section is open and comes back, because it is switched off somewhere else
   * (iOS Settings, the site's settings) and the boot may never have asked: it
   * only does when it opens to My location.
   */
  let blocked = $derived(
    here.permission === 'denied' || here.permission === 'insecure' || here.permission === 'unsupported',
  )

  $effect(() => {
    const check = () =>
      void geoPermission().then((p) => {
        // `unknown` says nothing new, and must not undo a refusal a failed fix found.
        if (p !== 'unknown') here.permission = p
      })
    check()
    return whileVisible(check, 60_000)
  })

  /**
   * My location's point: the one on screen if it is My location, else the
   * last fix. Neither while location is blocked: the last fix is then where
   * the phone was, not where it is, and the row says why it has no weather.
   */
  let herePoint = $derived(
    blocked ? null : location.selected?.place === 'here' ? location.selected : (prefs.lastFix ?? null),
  )

  $effect(() =>
    watchGlances(() => [...(herePoint ? [herePoint] : []), ...places.list]),
  )

  /**
   * The clock a row's radar is judged against (`glanceRow`): a frame over ten
   * minutes old stops speaking, as it does in the hero, even with no new
   * glance to say so.
   */
  let clock = $state(Date.now())
  $effect(() => whileVisible(() => (clock = Date.now()), 30_000))

  let hereEntry = $derived(glanceFor(herePoint))
  /** NWS's name for where the fix is, once the glance has it: "Boulder". */
  let hereName = $derived(hereEntry?.glance?.location.city ?? 'My location')

  let hereCurrent = $derived(!blocked && location.selected?.place === 'here')
  let currentId = $derived(shownPlace()?.id ?? null)
  let bells = $derived(pushSupport() === 'ready')

  let error = $state('')
  let busy = $state(false)

  async function pickHere() {
    error = ''
    busy = true
    try {
      error = (await locate(() => onPicked?.())) ?? ''
    } finally {
      busy = false
    }
  }

  function pick(place: SavedPlace) {
    error = ''
    onPicked?.()
    void openPlace(place)
  }

  function bell(place: SavedPlace) {
    // Straight from the click, with no await before it: the permission
    // prompt inside `enableAlerts` is allowed only during the tap.
    if (place.alerts) void disableAlerts(place.id)
    else void enableAlerts(place.id)
    // The same tap as Settings' bell, and the same feel (iOS I.5).
    haptic('impact')
  }

  /** The row as one sentence, name first, so a screen reader and a test both find it by name. */
  function label(name: string, entry: GlanceEntry | null, aside: string): string {
    const row = glanceRow(entry?.glance ?? null, clock)
    return [name, entry ? row.temp : '', row.line, row.highLow.replace('  ', ' '), aside].filter(Boolean).join(', ')
  }

  /** The station's time in the place's own zone, as the Forecast section's line has it. */
  function observed(entry: GlanceEntry | null): string {
    const at = entry?.glance?.current?.observed_at
    if (!at) return ''
    const d = new Date(at)
    const timeZone = entry?.glance?.location.timezone ?? undefined
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
  }

  /** What `here.permission` means, for the row with no weather. */
  let hereState = $derived(
    error ||
      (here.permission === 'denied'
      ? isNativeShell()
        ? 'Location is off for Fusion WX in iOS Settings'
        : 'Location is blocked in this browser'
      : here.permission === 'insecure' || here.permission === 'unsupported'
        ? 'Location is not available here'
        : 'Tap to find where you are'),
  )
</script>

{#snippet block(entry: GlanceEntry | null)}
  {@const row = glanceRow(entry?.glance ?? null, clock)}
  <span class="blk" class:untoned={!row.tone} data-tone={row.tone ?? undefined}>
    {entry ? row.temp : ''}
  </span>
{/snippet}

{#snippet who(name: string, entry: GlanceEntry | null, aside: string)}
  {@const row = glanceRow(entry?.glance ?? null, clock)}
  <span class="who">
    <b class="name">{name}</b>
    {#if row.line}<span class="c" class:alert-txt={row.alert}>{row.line}</span>{/if}
    <span class="m">
      {[row.highLow, aside].filter(Boolean).join(' · ')}
    </span>
  </span>
{/snippet}

<section class="places" aria-label="Places">
  <h2 class="wx-sec-head">
    Saved here
    <span class="hint">{places.list.length} of {MAX_PLACES}</span>
  </h2>

  <ul>
    <li class="place">
      <button
        type="button"
        class="pick touch-target"
        aria-current={hereCurrent ? 'true' : undefined}
        aria-label={label('My location', hereEntry, herePoint && !error && hereName !== 'My location' ? hereName : hereState)}
        disabled={busy}
        onclick={pickHere}
      >
        {#if herePoint}
          {@render block(hereEntry)}
        {:else}
          <span class="blk untoned"><Icon name="locate-fixed" size={26} /></span>
        {/if}
        {@render who(hereName, hereEntry, herePoint && !error ? 'My location' : hereState)}
      </button>
    </li>

    {#each places.list as place (place.id)}
      {@const entry = glanceFor(place)}
      <li class="place">
        <button
          type="button"
          class="pick touch-target"
          aria-current={currentId === place.id ? 'true' : undefined}
          aria-label={label(place.name, entry, observed(entry))}
          onclick={() => pick(place)}
        >
          {@render block(entry)}
          {@render who(place.name, entry, observed(entry))}
        </button>
        {#if bells}
          <button
            type="button"
            class="wx-ink-btn bell touch-target"
            aria-label={`Alerts for ${place.name}`}
            aria-pressed={!!place.alerts}
            disabled={push.busy}
            onclick={() => bell(place)}
          >
            <Icon name="bell" size={18} />
          </button>
        {/if}
      </li>
    {/each}
  </ul>

  <!-- A failed tap says why in the row itself; this line is for a screen reader. -->
  <p class="visually-hidden" role="alert">{error}</p>

  {#if places.list.length === 0}
    <p class="note">Save a place with the star beside its name, and it is listed here with its weather.</p>
  {:else}
    <p class="note">Saved in this browser. Share a link to move them to another device.</p>
  {/if}
  {#if onManage}
    <button type="button" class="manage" onclick={onManage}>Rename, reorder or share places in Settings</button>
  {/if}
</section>

<style>
  .places {
    padding: 16px var(--gutter) 24px;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .place {
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid var(--color-divider);
  }

  .pick {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 0;
    border: 0;
    background: none;
    color: var(--color-text);
    text-align: left;
    cursor: pointer;
  }

  .pick:disabled {
    opacity: 0.6;
    cursor: default;
  }

  /* The place on screen: the same inset ink bar the switcher used. */
  .pick[aria-current='true'] .blk {
    outline: 3px solid var(--color-text);
    outline-offset: 3px;
  }

  .blk {
    flex: none;
    width: 82px;
    height: 82px;
    display: grid;
    place-items: center;
    background: var(--color-field);
    color: var(--color-on-field);
    font-size: 29px;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  /* No answer: no colour, so a failed row never passes for a calm one. */
  .blk.untoned {
    background: none;
    border: 2px solid var(--color-divider);
    color: var(--color-muted);
  }

  .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .who > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    font-size: 20px;
    font-weight: 800;
  }

  .c {
    font-size: 15px;
  }

  .c.alert-txt {
    font-weight: 600;
  }

  .m {
    margin-top: 2px;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-muted);
    white-space: pre;
  }

  .bell {
    flex: none;
    width: 40px;
    height: 40px;
  }

  .note {
    margin: 14px 0 0;
    font-size: 14px;
    color: var(--color-muted);
  }


  .manage {
    margin-top: 12px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-text);
    font-size: 14px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }
</style>
